import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import MainLayout from "../Layouts/MainLayout";
import Breadcrumb from "../Breadcrumb";
import apiClient from "../../api/apiClient";
import { FiStar, FiMessageSquare, FiCode, FiSend } from "react-icons/fi";

const FeedbackView = () => {
  const { id } = useParams();
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFeedback = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.get(`/api/tasks/feedback/${id}/`);
        setFeedback(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching feedback:", error.response?.data || error.message);
        setError(error.response?.data?.message || "Failed to load feedback");
        setLoading(false);
      }
    };

      fetchFeedback();
  }, [id]);

  if (loading) return (
    <MainLayout>
      <div className="p-4 md:ml-64 lg:ml-64 xl:ml-64 h-auto pt-20 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    </MainLayout>
  );

  if (error) return (
    <MainLayout>
      <div className="p-4 md:ml-64 lg:ml-64 xl:ml-64 h-auto pt-20">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <p>Error: {error}</p>
        </div>
      </div>
    </MainLayout>
  );

  if (!feedback) return null;

  const { rate, feedback: reviewerFeedback, prompt, response } = feedback;

  return (
    <MainLayout>
      <div className="p-4 md:ml-64 lg:ml-64 xl:ml-64 h-auto pt-20">
        <div className="flex justify-start w-full mb-6">
          <Breadcrumb
            items={[
              { label: "Feedbacks", path: "/feedback" },
              { label: "Feedback View", path: `/feedback-view/${id}` },
            ]}
          />
        </div>
        <div className="flex justify-center w-full">
          <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl rounded-2xl shadow-sm border border-white/20 dark:border-gray-700/30 p-8 w-full max-w-4xl">
            <div id="feedback_box" className="flex flex-col w-full">
              {/* Rating and Feedback Section */}
              <div className="mb-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Rating Card */}
                  <div className="lg:col-span-1">
                    <div className="h-full bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 dark:from-indigo-900/30 dark:via-purple-900/30 dark:to-pink-900/30 rounded-2xl border border-indigo-200/50 dark:border-indigo-700/50 p-6 shadow-lg backdrop-blur-sm">
                      <div className="flex flex-col items-center justify-center h-full text-center">
                        <div className="bg-white/60 dark:bg-gray-800/60 rounded-full p-4 mb-4 shadow-md">
                          <FiStar className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                          Overall Rating
                        </h3>
                        <div className="text-4xl font-bold text-indigo-600 dark:text-indigo-400 mb-2">
                          {rate}
                        </div>
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <FiStar 
                              key={star} 
                              className={`w-4 h-4 ${
                                star <= rate 
                                  ? 'text-yellow-400 fill-current' 
                                  : 'text-gray-300 dark:text-gray-600'
                              }`} 
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Feedback Card */}
                  <div className="lg:col-span-2">
                    <div className="h-full bg-gradient-to-br from-blue-50 via-cyan-50 to-emerald-50 dark:from-blue-900/30 dark:via-cyan-900/30 dark:to-emerald-900/30 rounded-2xl border border-blue-200/50 dark:border-blue-700/50 p-6 shadow-lg backdrop-blur-sm">
                      <div className="flex flex-col h-full">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="bg-white/60 dark:bg-gray-800/60 rounded-full p-2 shadow-md">
                            <FiMessageSquare className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                            Reviewer Feedback
                          </h3>
                        </div>
                        <div className="flex-1 bg-white/40 dark:bg-gray-800/40 rounded-xl p-4 backdrop-blur-sm">
                          <p className="text-gray-700 dark:text-gray-200 leading-relaxed text-base">
                            {reviewerFeedback}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Prompt Section */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <FiCode className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Prompt</h2>
                </div>
                <div className="mt-4 p-6 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-200 dark:border-gray-700/30 overflow-x-auto text-gray-800 dark:text-white">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      code({ node, inline, className, children, ...props }) {
                        const match = /language-(\w+)/.exec(className || "");
                        return !inline && match ? (
                          <div className="overflow-x-auto rounded-lg">
                            <SyntaxHighlighter
                              style={atomDark}
                              showLineNumbers={true}
                              language={match[1]}
                              PreTag="div"
                              customStyle={{
                                margin: 0,
                                borderRadius: '0.5rem',
                                background: 'rgb(17, 24, 39)',
                              }}
                              {...props}
                            >
                              {String(children).replace(/\n$/, "")}
                            </SyntaxHighlighter>
                          </div>
                        ) : (
                          <code className={`${className} dark:text-white`} {...props}>
                            {children}
                          </code>
                        );
                      },
                     p: ({node, ...props}) => <p className="dark:text-white" {...props} />,
                     h1: ({node, ...props}) => <h1 className="dark:text-white font-bold text-2xl my-4" {...props} />,
                     h2: ({node, ...props}) => <h2 className="dark:text-white font-bold text-xl my-3" {...props} />,
                     h3: ({node, ...props}) => <h3 className="dark:text-white font-bold text-lg my-2" {...props} />,
                     ul: ({node, ...props}) => <ul className="dark:text-white list-disc pl-6 my-2" {...props} />,
                     ol: ({node, ...props}) => <ol className="dark:text-white list-decimal pl-6 my-2" {...props} />,
                     li: ({node, ...props}) => <li className="dark:text-white my-1" {...props} />,
                     a: ({node, ...props}) => <a className="dark:text-blue-400 underline" {...props} />,
                     blockquote: ({node, ...props}) => <blockquote className="dark:text-gray-300 border-l-4 border-gray-300 dark:border-gray-600 pl-4 py-2 my-2 italic" {...props} />,
                    }}
                  >
                    {prompt}
                  </ReactMarkdown>
                </div>
              </div>

              {/* Response Section */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <FiSend className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Response</h2>
                </div>
                <div className="mt-4 p-6 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-200 dark:border-gray-700/30 overflow-x-auto text-gray-800 dark:text-white">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      code({ node, inline, className, children, ...props }) {
                        const match = /language-(\w+)/.exec(className || "");
                        return !inline && match ? (
                          <div className="overflow-x-auto rounded-lg">
                            <SyntaxHighlighter
                              style={atomDark}
                              showLineNumbers={true}
                              language={match[1]}
                              PreTag="div"
                              customStyle={{
                                margin: 0,
                                borderRadius: '0.5rem',
                                background: 'rgb(17, 24, 39)',
                              }}
                              {...props}
                            >
                              {String(children).replace(/\n$/, "")}
                            </SyntaxHighlighter>
                          </div>
                        ) : (
                          <code className={`${className} dark:text-white`} {...props}>
                            {children}
                          </code>
                        );
                      },
                     p: ({node, ...props}) => <p className="dark:text-white" {...props} />,
                     h1: ({node, ...props}) => <h1 className="dark:text-white font-bold text-2xl my-4" {...props} />,
                     h2: ({node, ...props}) => <h2 className="dark:text-white font-bold text-xl my-3" {...props} />,
                     h3: ({node, ...props}) => <h3 className="dark:text-white font-bold text-lg my-2" {...props} />,
                     ul: ({node, ...props}) => <ul className="dark:text-white list-disc pl-6 my-2" {...props} />,
                     ol: ({node, ...props}) => <ol className="dark:text-white list-decimal pl-6 my-2" {...props} />,
                     li: ({node, ...props}) => <li className="dark:text-white my-1" {...props} />,
                     a: ({node, ...props}) => <a className="dark:text-blue-400 underline" {...props} />,
                     blockquote: ({node, ...props}) => <blockquote className="dark:text-gray-300 border-l-4 border-gray-300 dark:border-gray-600 pl-4 py-2 my-2 italic" {...props} />,
                    }}
                  >
                    {response}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default FeedbackView;